import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList, ActivityIndicator, TextInput } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96'; const SECONDARY = '#1e7be2';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`; }catch{} return c; });
const EP = { companyProfile:(id)=>`/api/company-profile/${id}`, deals:'/api/data/crm_deals', contacts:'/api/data/crm_contacts', stages:'/api/data/crm_stages', employees:'/api/employees', campaigns:'/api/campaigns' };
const getBusinessId = async()=>{ try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';} };
const safeData = r=>(r?.data?.success&&Array.isArray(r.data.data))?r.data.data:[];

export default function LeadCaptureScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [captureSources, setCaptureSources] = useState([]);
  const [captureStats, setCaptureStats] = useState({ total: 0, today: 0, thisWeek: 0, thisMonth: 0, topSource: 'N/A', conversionRate: 0 });
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', source: 'website', notes: '', value: '' });

  useEffect(()=>{ init(); },[]);
  const init = async()=>{ try{ setIsLoading(true); const b = route?.params?.company_id||await getBusinessId(); setCompanyId(b); await fetchAll(b); }catch(e){console.error(e);}finally{setIsLoading(false);} };
  const onRefresh = async()=>{ setRefreshing(true); await init(); setRefreshing(false); };

  const fetchAll = async(bizId)=>{
    const cid=parseInt(bizId)||0; const p={company_id:cid};
    const [profileRes,dealsRes,contactsRes] = await Promise.allSettled([api.get(EP.companyProfile(cid)),api.get(EP.deals,{params:p}),api.get(EP.contacts,{params:p})]);
    const profile=profileRes.status==='fulfilled'?(profileRes.value?.data?.success?(Array.isArray(profileRes.value.data.data)?profileRes.value.data.data[0]:profileRes.value.data.data):null):null;
    if(profile){setCompanyProfile(profile);await AsyncStorage.setItem('company_profile',JSON.stringify(profile));}
    else{try{const c=await AsyncStorage.getItem('company_profile');if(c)setCompanyProfile(JSON.parse(c));}catch{}}

    const deals=safeData(dealsRes.status==='fulfilled'?dealsRes.value:null);
    const cts=safeData(contactsRes.status==='fulfilled'?contactsRes.value:null);

    const allLeads=deals.map(d=>({
      id:String(d.id), name:d.contact_name||'Unknown', email:d.contact_email||'', phone:d.contact_phone||'',
      company:d.company||'', source:d.source||'direct', status:d.stage||'lead',
      value:parseFloat(d.value||0), probability:d.probability||0, createdAt:d.created_at,
    }));
    // Add contacts as captured leads too
    cts.forEach(c=>{ if(!allLeads.find(l=>l.email===c.email)){ allLeads.push({ id:`ct-${c.id}`, name:c.name||'', email:c.email||'', phone:c.phone||'', company:c.company||'', source:'crm', status:'captured', value:0, probability:c.score||0, createdAt:c.created_at }); }});
    setLeads(allLeads);

    // Source breakdown
    const srcMap={};
    allLeads.forEach(l=>{srcMap[l.source]=(srcMap[l.source]||0)+1;});
    setCaptureSources(Object.entries(srcMap).map(([s,c])=>({source:s,count:c,percentage:allLeads.length>0?(c/allLeads.length*100):0})).sort((a,b)=>b.count-a.count));

    const now=new Date(); const todayStart=new Date(now.getFullYear(),now.getMonth(),now.getDate());
    const weekStart=new Date(todayStart); weekStart.setDate(weekStart.getDate()-weekStart.getDay());
    const monthStart=new Date(now.getFullYear(),now.getMonth(),1);
    setCaptureStats({
      total:allLeads.length,
      today:allLeads.filter(l=>new Date(l.createdAt)>=todayStart).length,
      thisWeek:allLeads.filter(l=>new Date(l.createdAt)>=weekStart).length,
      thisMonth:allLeads.filter(l=>new Date(l.createdAt)>=monthStart).length,
      topSource:Object.entries(srcMap).sort((a,b)=>b[1]-a[1])[0]?.[0]||'N/A',
      conversionRate:allLeads.length>0?(allLeads.filter(l=>l.status==='closed_won'||l.status==='won').length/allLeads.length*100):0,
    });
  };

  const captureLead = async()=>{
    if(!formData.name||!formData.email||!formData.company){Alert.alert('Required','Name, email and company are required');return;}
    try{
      await api.post(EP.deals,{
        company:formData.company, contact_name:formData.name, contact_email:formData.email,
        contact_phone:formData.phone, stage:'lead', value:parseFloat(formData.value)||0,
        probability:10, source:formData.source, notes:formData.notes,
        company_id:parseInt(companyId)||0, tags:[],
      });
      Alert.alert('Success','Lead captured!');
      setShowAddModal(false); setFormData({name:'',email:'',phone:'',company:'',source:'website',notes:'',value:''});
      await fetchAll(companyId);
    }catch(e){Alert.alert('Error',e.message);}
  };

  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:0,maximumFractionDigits:0}).format(v||0);
  const formatDate=d=>d?new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short'}):'-';
  const getSourceIcon=s=>({website:'web',referral:'account-switch',linkedin:'linkedin',direct:'arrow-right','trade-show':'store','cold-call':'phone','email-campaign':'email','social-media':'share-variant',crm:'database'}[s]||'circle');
  const getSourceColor=s=>({website:'#3B82F6',referral:'#10B981',linkedin:'#0A66C2',direct:'#6B7280','trade-show':'#F59E0B','cold-call':'#EF4444','email-campaign':'#8B5CF6','social-media':'#EC4899',crm:'#6B7280'}[s]||'#6B7280');

  const renderHeader=()=>(
    <LinearGradient colors={['#059669','#10B981']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={st.headerBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF"/></TouchableOpacity>
        <View style={st.headerCenter}>
          <Text style={st.headerTitle}>{companyProfile?.name||'Lead Capture'}</Text>
          <Text style={st.headerSub}>{captureStats.total} captured leads</Text>
        </View>
        <TouchableOpacity onPress={()=>setShowAddModal(true)} style={st.headerBtn}><MaterialCommunityIcons name="plus" size={24} color="#FFF"/></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderStats=()=>(
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.statsRow} contentContainerStyle={{paddingHorizontal:16}}>
      {[{l:'Total',v:captureStats.total,c:'#059669'},{l:'Today',v:captureStats.today,c:'#3B82F6'},{l:'This Week',v:captureStats.thisWeek,c:'#F59E0B'},{l:'This Month',v:captureStats.thisMonth,c:'#8B5CF6'},{l:'Top Source',v:captureStats.topSource,c:'#EC4899'},{l:'Conversion',v:`${captureStats.conversionRate.toFixed(1)}%`,c:'#10B981'}].map((s2,i)=>(
        <View key={i} style={st.statCard}><Text style={[st.statValue,{color:s2.c}]}>{s2.v}</Text><Text style={st.statLabel}>{s2.l}</Text></View>
      ))}
    </ScrollView>
  );

  const renderTabs=()=>(
    <View style={st.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12}}>
      {[{k:'leads',t:'Captured Leads',i:'account-multiple'},{k:'sources',t:'Sources',i:'source-branch'},{k:'forms',t:'Capture Form',i:'form-textbox'}].map(tab=>(
        <TouchableOpacity key={tab.k} style={[st.tab,activeTab===tab.k&&st.activeTab]} onPress={()=>setActiveTab(tab.k)}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab===tab.k?'#059669':'#6B7280'}/>
          <Text style={[st.tabText,activeTab===tab.k&&{color:'#059669',fontWeight:'600'}]}>{tab.t}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  const renderLeads=()=>leads.length===0?(
    <View style={st.empty}><MaterialCommunityIcons name="account-search" size={48} color="#D1D5DB"/><Text style={st.emptyText}>No captured leads yet</Text>
      <TouchableOpacity style={st.addBtn} onPress={()=>setShowAddModal(true)}><Text style={st.addBtnText}>+ Capture Lead</Text></TouchableOpacity></View>
  ):(
    <View style={{padding:16}}>{leads.map((l,i)=>(
      <View key={i} style={st.leadCard}>
        <View style={{flexDirection:'row',alignItems:'center'}}>
          <View style={[st.srcDot,{backgroundColor:getSourceColor(l.source)}]}><MaterialCommunityIcons name={getSourceIcon(l.source)} size={14} color="#FFF"/></View>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={st.leadName}>{l.name}</Text>
            <Text style={st.leadMeta}>{l.company}{l.email?` · ${l.email}`:''}</Text>
          </View>
          <View style={{alignItems:'flex-end'}}>
            {l.value>0&&<Text style={st.leadValue}>{formatCurrency(l.value)}</Text>}
            <Text style={st.leadSource}>{l.source} · {formatDate(l.createdAt)}</Text>
          </View>
        </View>
      </View>
    ))}</View>
  );

  const renderSources=()=>(
    <View style={st.section}>
      <Text style={st.sectionTitle}>Capture Sources</Text>
      {captureSources.length===0?<Text style={st.emptyText}>No source data</Text>:
        captureSources.map((src,i)=>(
          <View key={i} style={st.sourceRow}>
            <View style={[st.srcDot,{backgroundColor:getSourceColor(src.source)}]}><MaterialCommunityIcons name={getSourceIcon(src.source)} size={14} color="#FFF"/></View>
            <Text style={st.sourceName}>{src.source}</Text>
            <View style={st.sourceBar}><View style={[st.sourceFill,{width:`${src.percentage}%`,backgroundColor:getSourceColor(src.source)}]}/></View>
            <Text style={st.sourceCount}>{src.count} ({src.percentage.toFixed(0)}%)</Text>
          </View>
        ))
      }
    </View>
  );

  const renderCaptureForm=()=>(
    <View style={st.section}>
      <Text style={st.sectionTitle}>Quick Capture Form</Text>
      {['name','email','phone','company','value','notes'].map(k=>(
        <View key={k} style={st.formField}>
          <Text style={st.formLabel}>{k.charAt(0).toUpperCase()+k.slice(1)}{['name','email','company'].includes(k)?' *':''}</Text>
          <TextInput style={st.formInput} placeholder={k==='value'?'Deal value (£)':k==='notes'?'Notes...':`Enter ${k}`} placeholderTextColor="#9CA3AF" value={formData[k]} onChangeText={v=>setFormData({...formData,[k]:v})} keyboardType={k==='email'?'email-address':k==='phone'?'phone-pad':k==='value'?'numeric':'default'} multiline={k==='notes'}/>
        </View>
      ))}
      <View style={st.formField}>
        <Text style={st.formLabel}>Source</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {['website','referral','linkedin','trade-show','cold-call','email-campaign','social-media'].map(src=>(
            <TouchableOpacity key={src} style={[st.srcChip,formData.source===src&&{backgroundColor:'#059669'}]} onPress={()=>setFormData({...formData,source:src})}>
              <MaterialCommunityIcons name={getSourceIcon(src)} size={14} color={formData.source===src?'#FFF':'#6B7280'}/>
              <Text style={[st.srcChipText,formData.source===src&&{color:'#FFF'}]}>{src}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
      <TouchableOpacity style={st.captureBtn} onPress={captureLead}>
        <MaterialCommunityIcons name="check" size={20} color="#FFF"/><Text style={st.captureBtnText}>Capture Lead</Text>
      </TouchableOpacity>
    </View>
  );

  const renderContent=()=>{
    const wrap=ch=><ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#059669']}/>} showsVerticalScrollIndicator={false}>{ch}<View style={{height:40}}/></ScrollView>;
    switch(activeTab){
      case 'leads':return wrap(renderLeads());
      case 'sources':return wrap(renderSources());
      case 'forms':return wrap(renderCaptureForm());
      default:return null;
    }
  };

  if(isLoading)return <SafeAreaView style={st.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color="#059669"/><Text style={st.loadingText}>Loading...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={st.container}><StatusBar barStyle="light-content"/>
      {renderHeader()}{renderStats()}{renderTabs()}{renderContent()}
    </SafeAreaView>
  );
}

const st=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingText:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{paddingTop:8,paddingBottom:16,paddingHorizontal:16},headerRow:{flexDirection:'row',alignItems:'center'},headerBtn:{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},headerCenter:{flex:1,marginLeft:12},headerTitle:{fontSize:20,fontWeight:'bold',color:'#FFF'},headerSub:{fontSize:13,color:'#A7F3D0',marginTop:2},
  statsRow:{flexGrow:0,flexShrink:0,backgroundColor:'#FFF',paddingVertical:12,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},statCard:{alignItems:'center',marginRight:16,minWidth:70},statValue:{fontSize:16,fontWeight:'bold'},statLabel:{fontSize:11,color:'#6B7280',marginTop:2},
  tabsRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,marginRight:4,borderRadius:20},activeTab:{backgroundColor:'#ECFDF5'},tabText:{fontSize:13,color:'#6B7280',marginLeft:6,fontWeight:'500'},
  section:{backgroundColor:'#FFF',margin:16,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},sectionTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12},
  empty:{alignItems:'center',padding:32},emptyText:{fontSize:16,color:'#6B7280',marginTop:12},addBtn:{backgroundColor:'#059669',paddingHorizontal:20,paddingVertical:10,borderRadius:8,marginTop:12},addBtnText:{color:'#FFF',fontWeight:'600'},
  leadCard:{backgroundColor:'#FFF',marginBottom:8,padding:12,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},leadName:{fontSize:15,fontWeight:'600',color:'#1F2937'},leadMeta:{fontSize:12,color:'#6B7280',marginTop:1},leadValue:{fontSize:14,fontWeight:'700',color:'#10B981'},leadSource:{fontSize:11,color:'#9CA3AF',marginTop:2},
  srcDot:{width:28,height:28,borderRadius:14,justifyContent:'center',alignItems:'center'},
  sourceRow:{flexDirection:'row',alignItems:'center',marginBottom:10},sourceName:{fontSize:13,color:'#1F2937',width:100,marginLeft:8},sourceBar:{flex:1,height:6,backgroundColor:'#E5E7EB',borderRadius:3,marginHorizontal:8,overflow:'hidden'},sourceFill:{height:'100%',borderRadius:3},sourceCount:{fontSize:12,fontWeight:'600',color:'#1F2937',width:70,textAlign:'right'},
  formField:{marginBottom:14},formLabel:{fontSize:14,fontWeight:'600',color:'#1F2937',marginBottom:6},formInput:{backgroundColor:'#F3F4F6',borderRadius:8,padding:12,fontSize:14,color:'#1F2937',borderWidth:1,borderColor:'#E5E7EB'},
  srcChip:{flexDirection:'row',alignItems:'center',paddingHorizontal:12,paddingVertical:6,borderRadius:16,backgroundColor:'#F3F4F6',marginRight:6},srcChipText:{fontSize:12,color:'#6B7280',marginLeft:4},
  captureBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#059669',borderRadius:10,padding:14,marginTop:8},captureBtnText:{color:'#FFF',fontWeight:'700',fontSize:16,marginLeft:8},
});